import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AtsName } from '@outlinked/shared';
import { listFindings, type FindingStatus, type FindingCategory, type AtsName as AtsNameType } from '../api/findings';
import { relativeTime } from '../lib/format';

const ATS_OPTIONS: Array<{ value: AtsNameType | ''; label: string }> = [
  { value: '', label: 'All ATSes' },
  ...AtsName.options.map((v) => ({ value: v as AtsNameType, label: v })),
];

const STATUS_OPTIONS: Array<{ value: FindingStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'triaging', label: 'Triaging' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pr_open', label: 'PR Open' },
  { value: 'pr_merged', label: 'PR Merged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const CATEGORY_COLORS: Record<FindingCategory, string> = {
  'ats-handler': 'bg-purple-100 text-purple-800',
  'llm-hallucination': 'bg-orange-100 text-orange-800',
  'linkedin-dom': 'bg-blue-100 text-blue-800',
  network: 'bg-cyan-100 text-cyan-800',
  auth: 'bg-red-100 text-red-800',
  budget: 'bg-yellow-100 text-yellow-800',
  unknown: 'bg-gray-100 text-gray-700',
};

interface FindingRowProps {
  f: {
    findingId: string;
    category: FindingCategory;
    status: string;
    summary: string;
    count: number;
    affectedUserIds: string[];
    lastSeenAt: string;
    prUrl?: string;
  };
  onNavigate: (id: string, status: string) => void;
}

export function FindingRow({ f, onNavigate }: FindingRowProps): React.ReactElement {
  return (
    <li
      onClick={() => onNavigate(f.findingId, f.status)}
      className="bg-white border border-border rounded-lg p-4 cursor-pointer hover:border-brand-500 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[f.category]}`}>
              {f.category}
            </span>
            <span className="text-xs text-text-muted">{f.status}</span>
          </div>
          <p className="text-sm font-medium text-text-primary">{f.summary}</p>
          <p className="text-xs text-text-muted mt-1">
            {f.count} occurrences · {f.affectedUserIds.length} users · last seen {relativeTime(f.lastSeenAt)}
          </p>
        </div>
        {f.prUrl && (
          <a
            href={f.prUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-brand-600 hover:underline shrink-0"
          >
            PR #{f.prUrl.split('/').pop()}
          </a>
        )}
      </div>
    </li>
  );
}

export function FindingsList(): React.ReactElement {
  // Default to 'open' so a fresh visit answers "what needs my attention right
  // now?" The user can switch to "All statuses" to see resolved/dismissed
  // history. Without this default, the no-status path goes through DDB Scan
  // and frequently returns an empty first page even when matches exist
  // (filter applies after the Scan's row budget — see listFindings handler).
  const [status, setStatus] = useState<FindingStatus | ''>('open');
  const [ats, setAts] = useState<AtsNameType | ''>('');
  const [cursor, setCursor] = useState<string | undefined>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['findings', { status, ats, cursor }],
    queryFn: () => listFindings({ status: status || undefined, ats: ats || undefined, cursor, limit: 50 }),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Findings</h1>
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as FindingStatus | ''); setCursor(undefined); }}
            className="text-sm border border-border rounded-md px-3 py-1.5"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={ats}
            onChange={(e) => { setAts(e.target.value as AtsNameType | ''); setCursor(undefined); }}
            className="text-sm border border-border rounded-md px-3 py-1.5"
          >
            {ATS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <ul role="list" className="space-y-3">
        {items.map((f) => (
          <FindingRow
            key={f.findingId}
            f={f}
            onNavigate={(id, s) => navigate(`/findings/${s}/${id}`)}
          />
        ))}
        {!isLoading && items.length === 0 && (
          <li className="text-sm text-center py-8 text-text-muted">No findings.</li>
        )}
      </ul>

      {data?.nextCursor && (
        <button onClick={() => setCursor(data.nextCursor ?? undefined)} className="text-sm text-brand-600 hover:underline">
          Next page →
        </button>
      )}
    </div>
  );
}
